/**
 * Forced password change (/change-password).
 *
 * Where AuthGate sends anyone holding a temporary credential. It is deliberately
 * a dead end apart from the form itself and signing out: a temporary password
 * has been read aloud across a desk or printed on a slip, so until it is
 * replaced the account is only as private as that slip.
 *
 * Reachable without the `mustChangePassword` flag too, so anyone can change
 * their password from Settings through the same screen.
 */
import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ds';
import {
  AUTH_BUTTON_STYLE,
  AuthError,
  AuthHeading,
  AuthLayout,
  TextField,
} from '@/components/auth/AuthLayout';
import { apiPost } from '@/lib/api';
import { MIN_PASSWORD_LENGTH, signOut } from '@/lib/session';
import { useAccount } from '@/hooks/useSession';
import { landingPathForAccount } from '@/lib/auth';
import { tenantPath } from '@/lib/tenant';
import { fontPx } from '@/lib/fontSize';

export function ChangePasswordPage() {
  const account = useAccount(true);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const forced = account.data?.mustChangePassword ?? false;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Choose a password of at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirm) {
      setError('Those two passwords don’t match.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      await apiPost('/auth/change-password', { currentPassword, newPassword });
      /*
       * A HARD navigation, for the same reason as the school login.
       *
       * The server has cleared `mustChangePassword` and re-issued the session
       * cookie (every other session was revoked). The client's cached account
       * still says the flag is set, and a router push would reach AuthGate
       * before a refetch could land — which sends the person straight back to
       * this page. Reloading rebuilds every cache from the new cookie.
       */
      window.location.assign(tenantPath(landingPathForAccount(account.data)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change your password.');
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
        <AuthHeading title={forced ? 'Choose your own password' : 'Change your password'}>
          {forced
            ? 'Your school gave you a temporary password. Pick your own to carry on — it should be something only you know.'
            : 'Signing in elsewhere will need the new password.'}
        </AuthHeading>

        <AuthError>{error}</AuthError>

        <TextField
          label={forced ? 'Temporary password' : 'Current password'}
          autoFocus
          required
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />

        <TextField
          label="New password"
          required
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          hint={`At least ${MIN_PASSWORD_LENGTH} characters. A short phrase you will remember beats a jumble you won’t.`}
        />

        <TextField
          label="Confirm new password"
          required
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />

        <Button type="submit" disabled={busy} style={AUTH_BUTTON_STYLE}>
          {busy ? 'Saving…' : 'Save password'}
        </Button>

        {/* The only other way out. Without it, someone who cannot remember the
            temporary password on a shared machine is stuck on this screen. */}
        <button
          type="button"
          onClick={() => void signOut()}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: fontPx(14),
            color: 'var(--text-hint)',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          Sign out instead
        </button>
      </form>
    </AuthLayout>
  );
}
